import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId, getUserId} from "../../utils/common"
import {setToastr} from "../App/appSlice"

export type TestStation = {
    testStationID: number
    regionID: number
    testStationName: string
    address1: string
    address2: string
    town: string
    postCode: string
    phone: string
    email: string
    active: boolean
    dealerID: number
    dealerDefault: boolean
    dealerName: string
}
export type Dealer = {
    dealerId: number
    regionId: number
    dealerNumber: string
    dealerName: string
    preparerId: string
    active: boolean
    hasLocalATF: boolean
    selected: boolean
}

export type addEditResponseType = {
    isAddEditSuccessful: boolean
    error: string
}

type TestStationState = {
    testStationSearchText: string
    loading: boolean
    testStationsData: TestStation[]
    testStationsGridError: string
    editTestStationData: TestStation[]
    editTestStationError: string
    addTestStationData: TestStation[]
    addTestStationError: string
    isAddEditModalOpen: boolean
    isModalLoading: boolean
    selectedTestStation: TestStation
    addEditResponse: addEditResponseType
    dealers: Dealer[]
    dealerDefaultSet: boolean
    gettingDealers: boolean
}

export const testStationInit = {
    testStationID: 0,
    regionID: 0,
    testStationName: "",
    address1: "",
    address2: "",
    town: "",
    postCode: "",
    phone: "",
    email: "",
    active: false,
    dealerID: 0,
    dealerDefault: false,
    dealerName: "",
}

export const addEditResponseInit = {
    isAddEditSuccessful: false,
    error: "",
}

const initialState: TestStationState = {
    testStationSearchText: "",
    loading: false,
    testStationsData: [],
    testStationsGridError: "",
    editTestStationData: [],
    editTestStationError: "",
    addTestStationData: [],
    addTestStationError: "",
    isAddEditModalOpen: false,
    isModalLoading: false,
    selectedTestStation: testStationInit,
    addEditResponse: addEditResponseInit,
    dealers: [],
    dealerDefaultSet: false,
    gettingDealers: false,
}

const testStationSlice = createSlice({
    name: "testStations",
    initialState,
    reducers: {
        setTestStationGridLoader(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        getTestStations(state, action: PayloadAction<TestStation[]>) {
            state.testStationsData = action.payload
        },
        setTestStationsGridError(state, action: PayloadAction<string>) {
            state.testStationsGridError = action.payload
        },
        editTestStationData(state, action: PayloadAction<TestStation[]>) {
            state.editTestStationData = action.payload
        },
        editTestStationError(state, action: PayloadAction<string>) {
            state.editTestStationError = action.payload
        },
        addTestStationData(state, action: PayloadAction<TestStation[]>) {
            state.addTestStationData = action.payload
        },
        addTestStationError(state, action: PayloadAction<string>) {
            state.addTestStationError = action.payload
        },
        addTestStation(state, action: PayloadAction<TestStation[]>) {
            state.testStationsData = action.payload
        },
        toggleAddEditModal(state, action: PayloadAction<boolean>) {
            if (action.payload) state.selectedTestStation = testStationInit
            state.isAddEditModalOpen = action.payload
        },
        toggleModalLoading(state, action: PayloadAction<boolean>) {
            state.isModalLoading = action.payload
        },
        setSelectedTestStation(state, action: PayloadAction<TestStation>) {
            state.selectedTestStation = action.payload
        },
        setDealers(state, action: PayloadAction<Dealer[]>) {
            state.dealers = action.payload
        },
        setAddEditResponse(state, action: PayloadAction<addEditResponseType>) {
            state.addEditResponse = action.payload
        },
        resetAddEditResponse(state) {
            state.addEditResponse = addEditResponseInit
        },
        setDealerDefaultSet(state, action: PayloadAction<boolean>) {
            state.dealerDefaultSet = action.payload
        },
        setGettingDealers(state, action: PayloadAction<boolean>) {
            state.gettingDealers = action.payload
        },
    },
})

export const {
    setTestStationGridLoader,
    getTestStations,
    setTestStationsGridError,
    editTestStationData,
    editTestStationError,
    addTestStationData,
    addTestStationError,
    toggleAddEditModal,
    toggleModalLoading,
    setSelectedTestStation,
    setAddEditResponse,
    resetAddEditResponse,
    setDealers,
    setDealerDefaultSet,
    setGettingDealers,
} = testStationSlice.actions

export default testStationSlice.reducer

export const getTestStationsById = (): AppThunk => (dispatch: any, getState) => {
    dispatch(setTestStationGridLoader(true))
    httpService(getAccessToken(getState()))
        .get(`regions/${getSelectedRegionId(getState())}/teststations`)
        .then((res: any) => {
            const testStation =
                res &&
                res.data.map((x: any) => {
                    const data = x.dealerDefault ? {...x, dealerName: x.dealerName + " - Default ATF"} : x
                    return data
                })
            dispatch(getTestStations(testStation))
        })
        .catch(error => dispatch(setTestStationsGridError(error)))
        .finally(() => dispatch(setTestStationGridLoader(false)))
}
export const fetchDealers =
    (dealerId: number): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setGettingDealers(true))
        httpService(getAccessToken(getState()))
            .get(`users/${getUserId(getState())}/regions/${getSelectedRegionId(getState())}/teststation/${dealerId}`)
            .then(response => {
                const fetchedDealer = response.data
                fetchedDealer.unshift({
                    dealerId: 0,
                    regionId: getSelectedRegionId(getState()),
                    dealerNumber: "",
                    dealerName: "",
                    preparerId: "",
                    active: "",
                    hasLocalATF: false,
                })
                dispatch(setDealers(fetchedDealer))
                dispatch(setGettingDealers(false))
            })
    }
export const editTestStation =
    (editTestStation: TestStation): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(toggleModalLoading(true))
        httpService(getAccessToken(getState()))
            .put(`regions/${getSelectedRegionId(getState())}/teststations`, editTestStation)
            .then(res => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Successfully Updated",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )

                dispatch(getTestStationsById())
                dispatch(toggleAddEditModal(false))
            })
            .catch(error =>
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                ),
            )
            .finally(() => {
                dispatch(toggleModalLoading(false))
            })
    }

export const addTestStation =
    (addTestStation: TestStation): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(toggleModalLoading(true))
        httpService(getAccessToken(getState()))
            .post(`regions/${getSelectedRegionId(getState())}/teststations`, addTestStation)
            .then(res => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Successfully Added",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
                dispatch(getTestStationsById())
                dispatch(toggleAddEditModal(false))
            })
            .catch(error =>
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                ),
            )

            .finally(() => {
                dispatch(toggleModalLoading(false))
            })
    }
